import { MunDropdown } from "@/components/design-system/MunDropdown";
import { MunPageContainer } from "@/components/layout/MunPageContainer";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useOnlineOrderHistory,
  usePosOrderHistory,
} from "@/hooks/useOrderHistory";
import {
  ORDER_STATUS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  PROVIDER_OPTIONS,
  TIME_SPAN_OPTIONS,
} from "@/lib/order-utils";
import { formatDateRangeForApi } from "@/lib/utils";
import {
  useExportOrderHistory,
  useExportPosOrderHistory,
} from "api/hooks/useOrderHistory";
import type {
  OrderSourceType,
  QueryParams,
} from "@/types/order-history/order-history-types";
import { TimeSpan } from "api/custom";
import type {
  OrderStatusEnum,
  PaymentMethod,
  PaymentTypeEnum,
} from "api/models";
import { ProviderEnum } from "api/models";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";
import {
  FilterField,
  FilterSelect,
  UnifiedFilters,
} from "./components/Filters";
import { OnlineOrdersTab, PosOrdersTab } from "./components/OrderTabs";

export function OrderHistoryPage() {
  const [activeTab, setActiveTab] = useState<OrderSourceType>("online");
  const [searchQuery, setSearchQuery] = useState("");
  const onlineFiltersHook = useOnlineOrderHistory();
  const posFiltersHook = usePosOrderHistory();
  const [onlineBusinessIds, setOnlineBusinessIds] = useState<number[]>([]);
  const [posBusinessId, setPosBusinessId] = useState<number | undefined>(
    undefined
  );
  const [onlineDateRange, setOnlineDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [posDateRange, setPosDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [onlineStatus, setOnlineStatus] = useState<OrderStatusEnum | undefined>(
    undefined
  );
  const [onlinePaymentType, setOnlinePaymentType] = useState<
    string | undefined
  >(undefined);
  const [onlineProviders, setOnlineProviders] = useState<ProviderEnum[]>([]);
  const [posPaymentType, setPosPaymentType] = useState<string | undefined>(
    undefined
  );
  const [posTimeSpan, setPosTimeSpan] = useState<TimeSpan>(TimeSpan.month);
  const [onlinePage, setOnlinePage] = useState(1);
  const [posPage, setPosPage] = useState(1);

  const exportOrders = useExportOrderHistory();
  const exportPosOrders = useExportPosOrderHistory();

  const handleResetFilters = () => {
    setOnlineBusinessIds([]);
    setPosBusinessId(undefined);
    setOnlineDateRange(undefined);
    setPosDateRange(undefined);
    setOnlineStatus(undefined);
    setOnlinePaymentType(undefined);
    setOnlineProviders([]);
    setPosPaymentType(undefined);
    setPosTimeSpan(TimeSpan.month);
    setSearchQuery("");
    setOnlinePage(1);
    setPosPage(1);
  };

  const onlineQueryParams: QueryParams = useMemo(() => {
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (onlineDateRange?.from) {
      startDate = new Date(onlineDateRange.from).toISOString();
    }
    if (onlineDateRange?.to) {
      endDate = new Date(onlineDateRange.to).toISOString();
    }

    return {
      businessIds: onlineBusinessIds,
      start: startDate,
      end: endDate,
      page: onlinePage,
      rowPerPage: 20,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orderStatus: onlineStatus ? [onlineStatus] : undefined,
      paymentType: onlinePaymentType
        ? ([onlinePaymentType] as PaymentTypeEnum[])
        : undefined,
      provider:
        onlineProviders && onlineProviders.length > 0 ? onlineProviders : undefined,
    };
  }, [
    onlineBusinessIds,
    onlineDateRange,
    onlinePage,
    onlineStatus,
    onlinePaymentType,
    onlineProviders,
  ]);

  const isPos = activeTab === "pos";
  const isExporting = isPos ? exportPosOrders.isPending : exportOrders.isPending;

  const exportDisabledReason = searchQuery
    ? "Clear the search box to export — search only filters the page you are viewing"
    : isPos
      ? !posBusinessId
        ? "Select a business to export"
        : posTimeSpan === TimeSpan.custom &&
            (!posDateRange?.from || !posDateRange?.to)
          ? "Pick both dates of the custom range to export"
          : undefined
      : onlineBusinessIds.length === 0
        ? "Select at least one business to export"
        : undefined;

  const handleExport = async () => {
    try {
      const { csvUrl, rowCount } = isPos
        ? await exportPosOrders.mutateAsync(posQueryParams)
        : await exportOrders.mutateAsync(onlineQueryParams);

      if (!rowCount) {
        toast.info("No orders match these filters");
        return;
      }

      window.open(csvUrl, "_blank");
      toast.success(`Exported ${rowCount} orders`);
    } catch {
      toast.error("Could not generate the CSV");
    }
  };

  const posQueryParams: QueryParams = useMemo(() => {
    const [startDate, endDate] = formatDateRangeForApi(
      posTimeSpan === TimeSpan.custom ? posDateRange : undefined
    );

    return {
      timeSpan: posTimeSpan,
      businessIds: posBusinessId ? [posBusinessId] : [],
      start: startDate,
      end: endDate,
      page: posPage,
      rowPerPage: 20,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orderStatus: undefined,
      paymentTypePos: posPaymentType
        ? ([posPaymentType] as PaymentMethod[])
        : undefined,
    };
  }, [posBusinessId, posDateRange, posPage, posPaymentType, posTimeSpan]);

  return (
    <MunPageContainer
      title="Transaction History"
      subtitle="View and manage your orders from all channels"
    >
      <div className="space-y-6">
        <UnifiedFilters
          activeTab={activeTab}
          onTabChange={setActiveTab}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={
            activeTab === "online"
              ? "Search by order number, provider, or customer name..."
              : "Search by order number, business, or customer name..."
          }
          onReset={handleResetFilters}
          onExport={handleExport}
          isExporting={isExporting}
          exportDisabledReason={exportDisabledReason}
        >
          {activeTab === "online" && (
            <>
              <FilterField label="Business">
                <MunDropdown
                  label="Business"
                  options={onlineFiltersHook.filters.businessOptions}
                  value={onlineFiltersHook.filters.businessOptions.filter((b) =>
                    onlineBusinessIds.includes(Number(b.value))
                  )}
                  onChange={(selected) => {
                    setOnlineBusinessIds(selected.map((s) => Number(s.value)));
                  }}
                  placeholder="Select businesses"
                  className="w-full"
                />
              </FilterField>
              <FilterField label="Date Range">
                <DateRangePicker
                  value={onlineDateRange}
                  onUpdate={setOnlineDateRange}
                  align="start"
                />
              </FilterField>
              <FilterField label="Status">
                <FilterSelect
                  value={onlineStatus}
                  onChange={setOnlineStatus}
                  options={ORDER_STATUS_OPTIONS}
                  placeholder="All statuses"
                  allLabel="All Statuses"
                />
              </FilterField>
              <FilterField label="Payment Type">
                <FilterSelect
                  value={onlinePaymentType}
                  onChange={setOnlinePaymentType}
                  options={PAYMENT_TYPE_OPTIONS}
                  placeholder="All payment types"
                  allLabel="All Payment Types"
                />
              </FilterField>
              <FilterField label="Providers">
                <MunDropdown
                  label="Providers"
                  options={PROVIDER_OPTIONS.filter(
                    (o) => o.value !== ProviderEnum.MunchiPos
                  )}
                  value={PROVIDER_OPTIONS.filter(
                    (o) =>
                      o.value !== ProviderEnum.MunchiPos &&
                      onlineProviders.includes(o.value)
                  )}
                  onChange={(selected) =>
                    setOnlineProviders(
                      selected.map((s) => s.value) as ProviderEnum[]
                    )
                  }
                  placeholder="All providers"
                  className="w-full"
                />
              </FilterField>
            </>
          )}

          {activeTab === "pos" && (
            <>
              <FilterField label="Business">
                <MunDropdown
                  label="Business"
                  multiple={false}
                  options={posFiltersHook.filters.businessOptions}
                  value={posBusinessId !== undefined ? String(posBusinessId) : ""}
                  onChange={(val) =>
                    setPosBusinessId(val ? Number(val) : undefined)
                  }
                  placeholder="Select business"
                  className="w-full"
                />
              </FilterField>
              <FilterField label="Time Period">
                <Select
                  value={posTimeSpan}
                  onValueChange={(value) => {
                    const selected = value as TimeSpan;
                    setPosTimeSpan(selected);
                    if (selected !== TimeSpan.custom) {
                      setPosDateRange(undefined);
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select time period" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SPAN_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              {posTimeSpan === TimeSpan.custom && (
                <FilterField label="Date Range">
                  <DateRangePicker
                    value={posDateRange}
                    onUpdate={setPosDateRange}
                    align="start"
                  />
                </FilterField>
              )}
              <FilterField label="Payment Type">
                <FilterSelect
                  value={posPaymentType}
                  onChange={setPosPaymentType}
                  options={PAYMENT_METHOD_OPTIONS}
                  placeholder="All payment types"
                  allLabel="All Payment Types"
                />
              </FilterField>
            </>
          )}
        </UnifiedFilters>

        {activeTab === "online" ? (
          <OnlineOrdersTab
            queryParams={onlineQueryParams}
            currentPage={onlinePage}
            onPageChange={setOnlinePage}
            searchQuery={searchQuery}
          />
        ) : (
          <PosOrdersTab
            queryParams={posQueryParams}
            currentPage={posPage}
            onPageChange={setPosPage}
            searchQuery={searchQuery}
          />
        )}
      </div>
    </MunPageContainer>
  );
}
